'use client';

import { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { FEED_TAB_DEFS, type FeedTab } from './FeedFilterBar';
import { useLang } from '@/lib/i18n/LanguageContext';

interface FeedLeftSidebarProps {
  activeTab?: FeedTab;
  activeDirection?: string;
  onTabChange?: (tab: FeedTab) => void;
  onDirectionFilter?: (direction: string) => void;
}

const DIRECTIONS: { value: 'BUY' | 'SELL'; label: string; icon: LucideIcon }[] = [
  { value: 'BUY', label: 'Long', icon: TrendingUp },
  { value: 'SELL', label: 'Short', icon: TrendingDown },
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="uppercase px-2 mb-2" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1.4px', color: 'var(--text-muted)' }}>
      {children}
    </p>
  );
}

// Owner swap 2026-07-26: the curated feed tabs (All Ideas, For You, …) moved HERE from the top
// of the feed, and the 4 market quick-filters that used to sit here moved up into that tab row.
//
// Trimmed 2026-09-01 (owner feedback): this used to also carry a "Navigate" section duplicating
// the global Navbar (Home/Leaderboard/Forum/…) and a static, fake "Moji kanali" list. Both are
// gone — this sidebar now holds only filters that are genuinely local to the feed view itself.
export default function FeedLeftSidebar({
  activeTab = 'all',
  activeDirection = 'ALL',
  onTabChange,
  onDirectionFilter,
}: FeedLeftSidebarProps) {
  const { t } = useLang();
  // Owner rule 2026-08-26: open by default on desktop; the chevron hides it and the
  // choice is remembered per browser. Mobile (<md) never shows this sidebar at all.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try { if (localStorage.getItem('smt_feed_sidebar') === 'collapsed') setCollapsed(true); } catch { /* ignore */ }
  }, []);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('smt_feed_sidebar', next ? 'collapsed' : 'open'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <aside
      className="hidden md:block shrink-0 transition-all duration-300 sticky self-start"
      style={{ width: collapsed ? 64 : 272, top: 64 }}
    >
      <div
        className="flex flex-col"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 18,
          padding: collapsed ? '18px 8px 12px' : '22px 14px 14px',
          maxHeight: 'calc(100vh - 84px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
      {/* Collapse toggle */}
      <div className="flex justify-end mb-2">
        <button
          onClick={toggleCollapsed}
          className="p-1.5 rounded-lg transition-colors duration-150"
          style={{ color: 'var(--text-muted)', background: 'transparent' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; }}
          title={collapsed ? t('Expand sidebar') : t('Collapse sidebar')}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Feed tabs (curated views) */}
      <div className="flex flex-col gap-0.5">
        {!collapsed && <SectionLabel>{t('Feed')}</SectionLabel>}
        {FEED_TAB_DEFS.map(({ id, label, icon: TabIcon }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => onTabChange?.(id)}
              title={collapsed ? t(label) : undefined}
              className="flex items-center transition-all duration-150 w-full text-left"
              style={{
                padding: '9px 14px',
                gap: 12,
                borderRadius: 11,
                background: isActive ? 'var(--accent-muted)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--text-body)',
                fontWeight: isActive ? 700 : 600,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-body)';
                }
              }}
            >
              <TabIcon size={15} className="shrink-0" style={{ color: isActive ? 'var(--accent)' : 'inherit' }} />
              {!collapsed && <span style={{ fontSize: '14.5px' }}>{t(label)}</span>}
            </button>
          );
        })}

        {/* Direction filters */}
        {!collapsed && (
          <div className="mt-3">
            <SectionLabel>{t('Direction')}</SectionLabel>
          </div>
        )}
        {collapsed && <div className="mt-2" />}
        {DIRECTIONS.map(({ value, label, icon: DirIcon }) => {
          const isActive = activeDirection === value;
          const tone = value === 'BUY' ? 'var(--bullish)' : 'var(--bearish)';
          const toneMuted = value === 'BUY' ? 'var(--bullish-muted)' : 'var(--bearish-muted)';
          return (
            <button
              key={value}
              onClick={() => onDirectionFilter?.(isActive ? 'ALL' : value)}
              title={collapsed ? label : undefined}
              className="flex items-center transition-all duration-150 w-full text-left"
              style={{
                padding: '9px 14px',
                gap: 12,
                borderRadius: 11,
                background: isActive ? toneMuted : 'transparent',
                color: isActive ? tone : 'var(--text-body)',
                fontWeight: isActive ? 700 : 600,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-body)';
                }
              }}
            >
              <DirIcon size={15} className="shrink-0" style={{ color: isActive ? tone : 'inherit' }} />
              {!collapsed && <span style={{ fontSize: '14.5px' }}>{label}</span>}
            </button>
          );
        })}
      </div>

      {/* Disclaimer */}
      {!collapsed && (
        <>
          <div className="mt-5 mb-4" style={{ height: 1, background: 'var(--border)' }} />
          <div style={{ background: 'var(--inverse-bg)', borderRadius: 16, padding: '18px 20px' }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.1px', color: 'var(--inverse-muted)', marginBottom: 8 }}>
              ⚠️ {t('REMINDER')}
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--inverse-body)' }}>
              {t('Signals are not investment advice. Only trade money you can afford to lose.')}
            </p>
          </div>
        </>
      )}
      </div>
    </aside>
  );
}
